import { useAppDispatch, useAppSelector } from "config/store";
import { authActions } from "config/store/modules/auth.ts";
import { tryAsyncFunc } from "globals/functions/async.ts";
import ajax from "config/ajax.ts";
import { useLocation, useNavigate } from "react-router-dom";
import { Form, Formik } from "formik";
import Button from "components/atom/button/Button.tsx";
import LoadingContainer from "components/container/LoadingContainer.tsx";
import Input from "components/atom/Input.tsx";
import { useTranslation } from "react-i18next";
import designerPng from "assets/images/designer.png";
import FormCheckbox from "components/form/FormCheckbox.tsx";
import { Fragment } from "react";
import { USER_TYPES } from "globals/constants.ts";
import iconUserGreenPng from "assets/images/icon-user-green.png";
import iconUserPng from "assets/images/icon-user.png";
import iconViewerPng from "assets/images/icon-viewer.png";
import iconViewerGreenPng from "assets/images/icon-viewer-green.png";
import { SignUpFormValuesType } from "utils/form.ts";

function Signup() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const currentLang = useAppSelector(state => state.global.currentLang);


  const values: SignUpFormValuesType = {
    name: "",
    email: "",
    password: "",
    type: USER_TYPES.VIEWER,
    acceptTerms: false,
    step: 1
  };

  //if we redirect to the signup page
  if (location.state && location.state.email) {
    values.email = location.state.email;
  }

  return (

    <div className="w-full">

      <Formik
        initialValues={values}
        validateOnChange={false}
        validateOnBlur={false}
        validate={values => {
          const errors: Partial<{ [K in keyof typeof values]: string }> = {};//same keys as type but with string value

          if (!values.type) {
            errors.type = t("forms.errors.requiredType");
          }

          //only on event manager
          if (values.type === USER_TYPES.EVENT_MANAGER) {
            if (!values.name) {
              errors.name = t("forms.errors.requiredName");
            }
          }

          if (!values.email) {
            errors.email = t("forms.errors.requiredEmail");
          }
          if (!values.password) {
            errors.password = t("forms.errors.requiredPassword");
          }

          if (!values.acceptTerms) {
            errors.acceptTerms = t("forms.errors.acceptTerms");
          }

          return errors;
        }}
        onSubmit={async (values, { setSubmitting, setStatus, setFieldError }) => {
          setSubmitting(true);
          setStatus(null);

          const [res, err] = await tryAsyncFunc(ajax.put("/public/auth/signup", {
            name: values.name,
            email: values.email,
            password: values.password,
            acceptCommunications: true,
            acceptTerms: true,//always accept since thats required
            type: values.type
          }));
          setSubmitting(false);

          if (err) {
            const errData = err.response.data || {};
            const errors = errData.errors || {};
            if (errors.global) {
              setStatus(errors.global);
              delete errors.global;
            }

            Object.keys(errors).forEach(errKey => {
              setFieldError(errKey, errors[errKey]);
            });
            return;
          }

          await dispatch(authActions.setJwt(res.data.jwt));
          await dispatch(authActions.setUser(res.data.user));

          if (location.state && location.state.redirect_to) {
            window.location.href = location.state.redirect_to;//hard refresh
            return;
          }

          if (res.data.user.unverifiedEmail && res.data.user.type === USER_TYPES.EVENT_MANAGER) {
            navigate({
              pathname: location.pathname,
              hash: "#/auth/verify-email"
            }, { state: { email: res.data.user.unverifiedEmail, coming_from: "signup" } });
            return;
          }

          navigate("/profile");

        }}
      >
        {({ isSubmitting, status, setFieldValue, values, errors }) => (
          <Fragment>
            <img src={designerPng} className="mx-auto block h-[220px] w-auto" />
            <h2 className="pb-6 text-center font-semibold">
              {values.step === 1 ? t("auth.profileChoice") : t("auth.signupTitle")}
            </h2>
            <LoadingContainer isLoading={isSubmitting} keepChildren>
              <Form>

                <div className={`${values.step === 1 ? "block" : "hidden"}`}>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <button type="button"
                            className={`border border-gray-200 rounded py-4 px-6 flex flex-col justify-center items-center ${values.type === USER_TYPES.VIEWER ? "bg-green-200 border-green-500" : "border-gray-200"}`}
                            onClick={() => setFieldValue("type", USER_TYPES.VIEWER)}>

                      <img alt="" src={iconViewerPng}
                           className={`w-12 h-12 min-w-12 ${values.type === USER_TYPES.VIEWER ? "block" : "hidden"}`} />
                      <img alt="" src={iconViewerGreenPng}
                           className={`w-12 h-12 min-w-12 ${values.type === USER_TYPES.VIEWER ? "hidden" : "block"}`} />

                      <h3 className="font-semibold pt-4">
                        {t("forms.viewer")}
                      </h3>
                      <p className="text-black p-1 text-xs">
                        {t("auth.viewerDescription")}
                      </p>
                    </button>
                    <button type="button"
                            className={`border rounded py-4 px-6 flex flex-col justify-center items-center ${values.type === USER_TYPES.EVENT_MANAGER ? "bg-green-200 border-green-500" : "border-gray-200"}`}
                            onClick={() => setFieldValue("type", USER_TYPES.EVENT_MANAGER)}>

                      <img alt="" src={iconUserPng}
                           className={`w-12 h-12 min-w-12 ${values.type === USER_TYPES.EVENT_MANAGER ? "block" : "hidden"}`} />
                      <img alt="" src={iconUserGreenPng}
                           className={`w-12 h-12 min-w-12 ${values.type === USER_TYPES.EVENT_MANAGER ? "hidden" : "block"}`} />

                      <h3 className="font-semibold pt-4">
                        {t("forms.eventManager")}
                      </h3>
                      <p className="text-black pt-1 text-xs">
                        {t("auth.eventManagerDescription")}
                      </p>
                    </button>
                  </div>
                  <div className="mt-4 flex flex-row items-center justify-between gap-4">
                    <Button style="normal" className="w-full"
                            onClick={() => setFieldValue("step", 2)}
                    >
                      {t("continue")}
                    </Button>
                  </div>
                  <div className="mt-4 flex flex-row items-center justify-between gap-4">
                    <Button small style="text" className="w-full"
                            to={{ pathname: location.pathname, hash: "#/auth/login" }}
                            state={location.state}
                    >
                      {t("auth.login")}
                    </Button>
                  </div>
                </div>

                <div className={`${values.step === 2 ? "block" : "hidden"}`}>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6">
                    <Input
                      forName="signup-name"
                      autoComplete="name" name="name" type="text"
                      label={values.type === USER_TYPES.EVENT_MANAGER ? t("forms.businessName") : t("forms.name")} />

                    <Input forName="signup-email" autoComplete="username" name="email" type="email"
                           label={t("forms.email")} />
                  </div>
                  <Input forName="signup-password" autoComplete="current-password" name="password" type="password"
                         label={t("forms.password")} />

                  <FormCheckbox name="acceptTerms"
                                forName="signup-acceptTerms"
                                checkboxClassName="w-6 h-6 min-w-6"
                                onChange={(checked) => setFieldValue("acceptTerms", checked)}
                                checked={values.acceptTerms}
                                error={errors.acceptTerms}
                                label={(
                                  <Fragment>
                                    {t("forms.acceptTerms")}<a className="underline"
                                                               href={`/terms`}
                                                               target="_blank">{t("forms.acceptTerms2")}</a>
                                  </Fragment>
                                )} />


                  {status && (
                    <p className="error block">{status}</p>
                  )}
                  <div className="mt-4 flex w-full flex-row items-center justify-center">
                    <Button className="w-full" type="submit" disabled={isSubmitting}>
                      {t("auth.signup")}
                    </Button>
                  </div>
                  <div className="mt-4 flex flex-row items-center justify-between gap-4">
                    <Button small style="text" className="w-full"
                            onClick={() => setFieldValue("step", 1)}
                    >
                      {t("return")}
                    </Button>
                  </div>
                </div>
              </Form>
            </LoadingContainer>
          </Fragment>
        )}
      </Formik>
    </div>
  );
}

export default Signup;
